import React, { useEffect } from 'react';
import config from '../config/config';
import { getResultLink } from '../utils/searchResultLink';
import { copyToClipboard } from '../services/clipboardService';

// アイコンのURL定義
const ICON_URLS = {
  close: `${config.icons.baseUrl}${config.icons.close}`,
  star: `${config.icons.baseUrl}${config.icons.star}`,
  share: `${config.icons.baseUrl}${config.icons.share}`,
  arrowTopRight: `${config.icons.baseUrl}${config.icons.arrowTopRight}`,
  directionsTrain: `${config.icons.baseUrl}${config.icons.directionsTrain}`,
  location: `${config.icons.baseUrl}${config.icons.location}`,
  store: `${config.icons.baseUrl}${config.icons.store}`,
  wc: `${config.icons.baseUrl}${config.icons.wc}`,
  restaurant: `${config.icons.baseUrl}${config.icons.restaurant}`,
  localCafe: `${config.icons.baseUrl}${config.icons.localCafe}`,
  shoppingCart: `${config.icons.baseUrl}${config.icons.shoppingCart}`,
  hotel: `${config.icons.baseUrl}${config.icons.hotel}`,
  directionsBus: `${config.icons.baseUrl}${config.icons.directionsBus}`,
  localParking: `${config.icons.baseUrl}${config.icons.localParking}`,
  mountain: `${config.icons.baseUrl}${config.icons.mountain}`,
  keyboardBackspace: `${config.icons.baseUrl}${config.icons.keyboardBackspace}`,
  importContacts: `${config.icons.baseUrl}${config.icons.importContacts}`,
  map: `${config.icons.baseUrl}${config.icons.map}`,
};

// 電話帳リンクのtargetとrel属性
const LINK_TARGET = config.search.phonebook.linkTarget;
const LINK_REL = config.search.phonebook.linkRel;

// 共有ボタンのURLで使用
const MAPION_BASE_URL = config.urls.mapionBaseUrl;
const MAPION_M2_URL = MAPION_BASE_URL + process.env.PUBLIC_URL;
const DEFAULT_PITCH = config.map.defaultPitch;
const DEFAULT_BEARING = config.map.defaultBearing;

/**
 * 検索結果コンポーネント
 * 検索結果が1件のケースと複数件のケースで表示内容が異なる
 * 複数件が表示されている時、どれかをクリックするとその1件の表示になる
 * 1件表示とした後に戻るボタンで、直前に表示されていた複数件が再表示される
 * モバイルとデスクトップで画面内の検索結果の配置が異なる
 */
const SearchResult = ({
  result,
  searchQuery,
  handleCloseSearchResult,
  handleResultSelect,
  selectedIndex,
  setSelectedIndex,
  handleSelectMultiple,
  isSearchResultVisible,
  setIsSearchResultVisible,
  hoveredIndex,
  setHoveredIndex,
  setIsResearchHereEnabled,
  isTablet,
  setNaviRoute,
  saveSpot
}) => {

  const items = result?.data?.items || [];

  useEffect(() => {
    if (items.length === 1 && selectedIndex === null) {
      setIsResearchHereEnabled(false);
    }
  }, [items.length, selectedIndex === null, setIsResearchHereEnabled]);

  // 早期リターン: 結果がなければここでは検索結果のパネルを表示しない
  if (!result) {
    return null;
  }

  // モバイルで折りたたみ中なら付けるクラス
  let collapsedClass = '';
  if (isTablet && !isSearchResultVisible) {
    collapsedClass = ' is-collapsed';
  }

  // ----- ヘルパー関数 -----

  // 名称表示部分を共通化：リンクがあればaタグ、なければspan
  const renderName = (name, href) => {
    // リンククリック時に親要素のイベントが発火しないようにする
    const handleClick = (e) => {
      e.stopPropagation();
    };

    return href ? (
      <span
        className="result-name-wrap"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
      >
        <a
          className='result-name'
          href={href}
          target={LINK_TARGET}
          rel={LINK_REL}
          onClick={handleClick}
        >
          {name}
        </a>
        {/* 電話帳アイコン（同じリンク先） */}
        <a
          className="phonebook-link"
          href={href}
          target={LINK_TARGET}
          rel={LINK_REL}
          onClick={handleClick}
          style={{ display: 'inline-block' }}
        >
          <i
            className="svg-icon-blue"
            style={{
              backgroundImage: `url('${ICON_URLS.importContacts}')`,
            }}
          ></i>
        </a>
      </span>
    ) : (
      <span className='result-name'>{name}</span>
    );
  };

  // 共有ボタンのURLを構築する
  const buildShareUrl = (item) => {

    // zoomはitemに無いので設定値を使用
    const zoom = Number(config.map.zoomLevel.shareUrlZoomLevel);

    // itemで保持しているのはlat,lon
    const lat = Number(item.lat);
    const lon = Number(item.lon);
    const pitch = DEFAULT_PITCH;
    const bearing = DEFAULT_BEARING;
    const poi = item.poi_code || item.station_code || '';

    const coordsPath = `/${zoom}/${lat}/${lon}/${pitch}/${bearing}`;
    const query = poi ? `?poi=${encodeURIComponent(poi)}` : '';
    return `${MAPION_M2_URL}${coordsPath}${query}`;
  };

  // ----- レンダリング関数群 -----

  // 1件表示用の関数
  const renderSingleResult = (item) => {
    const displayName = item.name || item.poi_name || searchQuery || '';
    const href = getResultLink(item);

    // const panelClassName = isTablet ? 'search-result-single-panel' : 'desktop-search-result-single-panel';

    // 結果表示パネルクラス
    // モバイル・タブレットとPCの両者で表示の仕方が大きく異なるので、スタイルで差をつけるのではなく別クラスとして扱う
    // まずPC用で初期化
    let singleResultPanelClassName = 'desktop-search-result-single-panel';
    // モバイル・タブレットなら上書き
    if (isTablet) {
      singleResultPanelClassName = 'search-result-single-panel' + collapsedClass;
    }

    let { poi_code, lat, lon, taxonomy, address } = item;
    return (
      <>
        {/* PC:desktop-search-panel モバイル:search-result-panel */}
        <div id="search-result-single-panel" className={singleResultPanelClassName}>
          <div className="search-result-header">

            {/* 戻るボタン or ダミー要素 */}
            {selectedIndex !== null && result.data.items.length > 1 ? (
              <button
                className="back-btn"
                onClick={() => {
                  // 複数件表示に戻す（選択リセットはhandleSelectMultiple内で実施）
                  handleSelectMultiple();
                }}
              >
                <i
                  className="svg-icon-blue"
                  style={{
                    backgroundImage: `url('${ICON_URLS.keyboardBackspace}')`,
                  }}
                ></i>
              </button>
            ) : (
              // ダミー要素（戻るボタンと同じ幅を確保）
              <span
                style={{ display: 'inline-block', width: 34, marginRight: '10px' }}>
              </span>
            )}

            {/* 名称のところ、電話帳へのリンクがあれば付ける */}
            <h3 id="search-result-title">
              {renderName(displayName, href)}
            </h3>

            {/* モバイル・タブレットでのボタンは折り畳み */}
            {/* PCでのボタンは閉じる */}
            {isTablet ? (
              <button
                type="button"
                className="collapse-search-result-btn"
                onClick={() => setIsSearchResultVisible(false)}
              >
                <span className="collapse-search-result-label">地図</span>
                <i
                  className="svg-icon-blue"
                  style={{ backgroundImage: `url('${ICON_URLS.map}')` }}
                ></i>
              </button>
            ) : (
              <button
                type="button"
                className="close-search-result-btn"
                onClick={handleCloseSearchResult}>
                <i
                  className="svg-icon-black"
                  style={{ backgroundImage: `url('${ICON_URLS.close}')` }}
                ></i>
              </button>
            )}
          </div>

          <div className="search-result-content">
            <div id="search-result-details">
              <div className="result-details">

                {item.address && item.address !== displayName && item.taxonomy !== 'address' && (
                  <div>
                    <span className="detail-text address">{item.address}</span>
                  </div>
                )}
                {item.phone_number && (
                  <div
                    className="detail-text phone"
                    style={{ cursor: 'pointer' }}
                    onClick={() => window.open(`tel:${item.phone_number}`)}
                  >
                    {item.phone_number}
                  </div>
                )}

                {/* アクションボタン */}
                <div className="search-result-actions-compact">
                  <button
                    className="action-btn save"
                    id="save-spot-btn"
                    onClick={() => {
                      let name = displayName;
                      if (taxonomy === "zip") {
                        name = address // 郵便番号の場合は番号にしても意味がないので住所をnameとする
                        poi_code = null; // POIコードもただの郵便番号なので登録しない
                      }
                      saveSpot({ name, poi_code, address, lat, lng: lon });
                    }}
                  >
                    <i
                      className="svg-icon-white"
                      style={{ backgroundImage: `url(${ICON_URLS.star})` }}>
                    </i>
                    保存
                  </button>

                  <button
                    className="action-btn share"
                    id="share-spot-btn"
                    onClick={async () => {
                      try {
                        const url = buildShareUrl(item);
                        const result = await copyToClipboard(url, 'url');
                        alert(result?.message);
                      } catch (err) {
                        alert('URL のコピーに失敗しました');
                      }
                    }}
                  >
                    <i
                      className="svg-icon-white"
                      style={{ backgroundImage: `url(${ICON_URLS.share})` }}
                    >
                    </i>
                    共有
                  </button>

                  <button
                    className="action-btn route"
                    id="route-search-btn"
                    onClick={() => {
                      let name = taxonomy === "zip" && address ? address : displayName; // 郵便番号の場合は番号にしても意味がないので住所をnameとする
                      setNaviRoute({ name, lat, lon });
                    }}
                  >
                    <i
                      className="svg-icon-white"
                      style={{ backgroundImage: `url(${ICON_URLS.arrowTopRight})` }}>
                    </i>
                    ルート
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // 複数件表示用の関数
  const renderMultipleResults = (items) => {

    // 結果表示パネルクラス
    // モバイル・タブレットとPCの両者で表示の仕方が大きく異なるので、スタイルで差をつけるのではなく別クラスとして扱う
    // まずPC用で初期化
    let multiResultPanelClassName = 'desktop-search-result-panel';
    // モバイル・タブレットなら上書き
    if (isTablet) {
      multiResultPanelClassName = 'search-result-panel' + collapsedClass;
    }

    return (
      <>
        {/* PC:desktop-search-result-panel モバイル:search-result-panel */}
        <div id="search-result-panel" className={multiResultPanelClassName}>
          <div className="search-result-header">
            <h3 id="search-result-title">
              {`"${searchQuery || ''}" の検索結果 (${items.length}件表示)`}
            </h3>

            {/* モバイル・タブレットでのボタンは折り畳み */}
            {/* PCでのボタンは閉じる */}
            {isTablet ? (
              <button
                type="button"
                className="collapse-search-result-btn"
                onClick={() => setIsSearchResultVisible(false)}
              >
                <span className="collapse-search-result-label">地図</span>
                <i
                  className="svg-icon-blue"
                  style={{ backgroundImage: `url('${ICON_URLS.map}')` }}
                ></i>
              </button>
            ) : (
              <button
                type="button"
                className="close-search-result-btn"
                onClick={handleCloseSearchResult}
              >
                <i
                  className="svg-icon-black"
                  style={{ backgroundImage: `url('${ICON_URLS.close}')` }}
                ></i>
              </button>
            )}
          </div>

          <div className="search-result-content">
            <div id="search-result-details">
              {items.map((data, index) => {
                const displayName = data.poi_name || data.name || '名称不明';

                // 距離（km表示、小数点1桁まで）
                const distanceText = (typeof data.distance === 'number')
                  ? `${(data.distance / 1000).toFixed(1)}km`
                  : '';

                return (
                  // 検索結果のうちの1件1件を作成しているところ
                  <div
                    className={`result-card clickable-result${hoveredIndex === index ? ' hovered' : ''}`}
                    data-lat={data.lat}
                    data-lon={data.lon}
                    data-id={data.poi_code || data.id}
                    data-index={index}
                    key={index}
                    onClick={() => {
                      // 1件選択時には、選択したindexをセットして、検索結果選択時の処理を行う（Map.jsx）
                      setSelectedIndex(index);
                      handleResultSelect(data);
                    }}
                    // マウスオーバー時にスタイル変更
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    <div className="result-header">
                      <h3 className="result-title">
                        <i
                          className="svg-icon-blue"
                          style={{
                            backgroundImage: `url('${ICON_URLS.location}')`,
                            width: 16,
                            height: 16,
                            display: 'inline-block',
                            verticalAlign: 'middle'
                          }}
                        ></i>
                        <span className='result-name'>{displayName}</span>
                        {distanceText && (
                          <span className="result-distance" style={{ marginLeft: 8, color: '#666' }}>
                            {distanceText}
                          </span>
                        )}
                      </h3>
                    </div>

                    {/* カテゴリ名 */}
                    <div className="result-details">
                      <div className="detail-item">
                        <span className="detail-text" style={{ marginTop: 4, display: 'block', textAlign: 'left' }}>
                          {Array.isArray(data.category2_name) && data.category2_name[0]
                            ? data.category2_name[0]
                            : Array.isArray(data.category1_name) && data.category1_name[0]
                              ? data.category1_name[0]
                              : ''}
                        </span>
                      </div>

                      {/* 住所 */}
                      <div className="detail-item">
                        {data.address && data.taxonomy !== 'address' && (
                          <span className="detail-text address">{data.address}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </>
    );
  }
  // ----- 表示条件分岐 -----

  // ケース1: 1件選択の場合
  if (selectedIndex !== null) {
    return renderSingleResult(result.data.items[selectedIndex]);
  }

  // ケース2: 検索結果が複数件の場合
  if (result.data.items.length > 1) {
    return renderMultipleResults(result.data.items);
  }

  // ケース3: 検索結果が1件のみの場合
  if (result.data.items.length === 1) {
    return renderSingleResult(result.data.items[0]);
  }

  // ケース4: 検索結果が0件の場合
  return null;
}

export default SearchResult;